import RealtimeKitClient from '@cloudflare/realtimekit';
import { createRemoteAudioPlayer } from 'shared/helpers/realtimeKitAudio';

const createCallDisconnectedEvent = () => new CustomEvent('call:disconnected');
const createCallPeerLeftEvent = () => new CustomEvent('call:peer-left');
const createCallAudioTracksEvent = () => new CustomEvent('call:audio-tracks');

// Singleton Cloudflare RealtimeKit client for dashboard web calls — mirrors
// TwilioVoiceClient's shape (EventTarget singleton, `call:disconnected` event)
// so useCallSession's global listener wiring works unchanged across providers.
class RealtimeKitCallClient extends EventTarget {
  constructor() {
    super();
    this.meeting = null;
    this.detachRemoteAudio = null;
    this.muted = false;
    this.holdApplied = false;
  }

  async connect(token) {
    if (this.meeting) {
      try {
        this.detachRemoteAudio?.();
        this.detachRemoteAudio = null;
        await this.meeting.leave();
      } catch (error) {
        // Best-effort: leaving a dead/dangling session should never block
        // a fresh connect.
      }
    }

    const meeting = await RealtimeKitClient.init({
      authToken: token,
      defaults: { audio: true, video: false },
    });
    this.meeting = meeting;
    meeting.self.on('roomLeft', () => {
      // Guard against a stale meeting still firing events after a newer
      // connect() (or a disconnect()) has already replaced/cleared it.
      if (this.meeting !== meeting) return;
      this.detachRemoteAudio?.();
      this.detachRemoteAudio = null;
      this.dispatchEvent(createCallDisconnectedEvent());
      this.meeting = null;
    });
    // The customer has no cable connection to learn the agent hung up, so the
    // widget/call-page detect it locally via this same participantLeft signal.
    // Mirror that here for the reverse direction: if the customer leaves the
    // room, tell useCallSession to end the call (backend + local UI teardown)
    // instead of leaving the agent's widget stuck showing an active call.
    meeting.participants.joined.on('participantLeft', () => {
      if (this.meeting !== meeting) return;
      if (meeting.participants.joined.toArray().length > 0) return;
      this.dispatchEvent(createCallPeerLeftEvent());
    });
    // The call recorder (see webCallClient) needs to hear whenever either
    // side's audio track appears or is replaced — joining, mute, hold.
    const announceAudio = () => {
      if (this.meeting !== meeting) return;
      this.dispatchEvent(createCallAudioTracksEvent());
    };
    const watchParticipantAudio = participant => {
      participant.on('audioUpdate', announceAudio);
      announceAudio();
    };
    meeting.self.on('audioUpdate', announceAudio);
    meeting.participants.joined.on('participantJoined', watchParticipantAudio);
    await meeting.join();
    this.detachRemoteAudio = createRemoteAudioPlayer(meeting);
    // The customer is usually in the room before we join, so participantJoined
    // never fires for them.
    meeting.participants.joined.toArray().forEach(watchParticipantAudio);
  }

  async disconnect() {
    if (!this.meeting) return;
    this.detachRemoteAudio?.();
    this.detachRemoteAudio = null;
    await this.meeting.leave();
    this.meeting = null;
  }

  async setMuted(muted) {
    if (!this.meeting) return;
    this.muted = muted;
    if (muted) {
      await this.meeting.self.disableAudio();
    } else {
      await this.meeting.self.enableAudio();
    }
  }

  // Agent-side hold: stop playing the customer's audio and stop publishing our
  // own. RealtimeKit exposes no server-side hold, so this IS the hold (design
  // 8.2) and the resulting request tells the server it was applied.
  async setHold(held) {
    if (!this.meeting) return;
    this.holdApplied = held;
    if (held) {
      this.detachRemoteAudio?.();
      this.detachRemoteAudio = null;
      await this.meeting.self.disableAudio();
      return;
    }
    this.detachRemoteAudio ||= createRemoteAudioPlayer(this.meeting);
    // An agent who was muted before the hold stays muted after it.
    if (!this.muted) await this.meeting.self.enableAudio();
  }

  // Every party's audio for the call recorder — a conference transfer can put
  // two agents plus the customer in one room, so every joined participant
  // with an audio track counts, not just the first.
  getAudioTracks() {
    const remotes = this.meeting
      ? this.meeting.participants.joined
          .toArray()
          .filter(participant => participant.audioTrack)
      : [];
    return {
      localTrack: this.meeting?.self.audioTrack || null,
      remoteTracks: remotes.map(participant => participant.audioTrack),
    };
  }

  get isConnected() {
    return Boolean(this.meeting);
  }
}

export default new RealtimeKitCallClient();
